import { useMemo } from 'react';

import {
  ExperienceEntityType,
  useCanViewCampaigns,
  useExperienceEntityVenues,
  useExperienceModuleContext,
} from 'modules/experience-core';
import { NavigationItem } from 'modules/experience-core';
import {
  useCanViewBestOfWolt,
  useCanViewBusinessDetails,
  useCanViewPurchase,
  useCanViewTranslation,
  useCanViewPickingRoute,
  useCanViewSettlements,
} from 'modules/experience-core';

import {
  BEST_OF_WOLT_NAVIGATION_ITEM,
  BUSINESS_DETAILS_NAVIGATION_ITEM,
  PURCHASES_NAVIGATION_ITEM,
  SETTLEMENTS_NAVIGATION_ITEM,
  TRANSLATION_NAVIGATION_ITEM,
  PICKING_ROUTE_NAVIGATION_ITEM,
  VENUE_CAMPAIGNS_NAVIGATION_ITEM,
} from '../constants';

export const useVenueNavigationItems = (): NavigationItem[] => {
  const { experienceEntity } = useExperienceModuleContext();
  const venues = useExperienceEntityVenues();
  const filteredExperienceVenueEntities =
    experienceEntity.experienceEntityType === ExperienceEntityType.VENUE
      ? [experienceEntity]
      : venues.filter(venueExperienceEntity => venueExperienceEntity.experienceEntityObject.status !== 'VENUE_DELETED');
  const filteredVenues = filteredExperienceVenueEntities.map(
    venueExperienceEntity => venueExperienceEntity.experienceEntityObject,
  );
  const { isBestOfWoltEnabled, hasPermissionsForVenues: hasBoWPermissions } = useCanViewBestOfWolt(
    filteredExperienceVenueEntities,
  );
  const shouldShowBestOfWoltNavigation = isBestOfWoltEnabled && hasBoWPermissions;
  const shouldShowBusinessDetailsNavigation = useCanViewBusinessDetails(filteredExperienceVenueEntities);
  const shouldShowPurchaseNavigation = useCanViewPurchase(filteredExperienceVenueEntities);
  const shouldShowTranslationNavigation =
    useCanViewTranslation(experienceEntity).hasPermissions && filteredVenues.length > 0;
  const shouldShowPickingRouteNavigation = useCanViewPickingRoute(filteredExperienceVenueEntities);
  const shouldShowCampaignNavigation =
    useCanViewCampaigns(experienceEntity, filteredExperienceVenueEntities) &&
    experienceEntity.experienceEntityType !== ExperienceEntityType.MERCHANT;
  const shouldShowSettlementsNavigation = useCanViewSettlements(filteredExperienceVenueEntities);

  return useMemo(
    () => [
      ...(shouldShowBestOfWoltNavigation ? [BEST_OF_WOLT_NAVIGATION_ITEM] : []),
      ...(shouldShowBusinessDetailsNavigation ? [BUSINESS_DETAILS_NAVIGATION_ITEM] : []),
      ...(shouldShowPurchaseNavigation ? [PURCHASES_NAVIGATION_ITEM] : []),
      ...(shouldShowTranslationNavigation ? [TRANSLATION_NAVIGATION_ITEM] : []),
      ...(shouldShowCampaignNavigation ? [VENUE_CAMPAIGNS_NAVIGATION_ITEM] : []),
      ...(shouldShowPickingRouteNavigation ? [PICKING_ROUTE_NAVIGATION_ITEM] : []),
      ...(shouldShowSettlementsNavigation ? [SETTLEMENTS_NAVIGATION_ITEM] : []),
    ],
    [
      shouldShowBestOfWoltNavigation,
      shouldShowBusinessDetailsNavigation,
      shouldShowPurchaseNavigation,
      shouldShowTranslationNavigation,
      shouldShowCampaignNavigation,
      shouldShowPickingRouteNavigation,
      shouldShowSettlementsNavigation,
    ],
  );
};
